<template>
  <v-container
    id="transactions-view"
    fluid
    tag="section"
  >
    <material-card
      icon="$scheduled_transactions"
      icon-small
      color="accent"
      :title="page_title"
      help="scheduled_transactions"
    >
      <template v-slot:title2>
        <tooltip-btn
          icon="$back"
          tooltip="transactions"
          @click="back"
        />
      </template>
      <v-card-title>
        <v-btn
          v-show="editor"
          v-shortkey.once="['f2']"
          color="primary"
          class="mb-2 ml-2"
          @shortkey="edit('new')"
          @click="edit('new')"
        >
          {{ $t('new_item',{ item: $t('scheduled_transaction') }) }}
        </v-btn>
        <v-spacer />
        <v-switch
          v-model="show_scheduled"
          class="mx-2"
          :label="$t('show_scheduled')"
          hide-details
        />
        <v-text-field
          v-model="search"
          append-icon="$search"
          class="ml-auto"
          hide-details
          :label="$t('search_records')"
          single-line
        />
      </v-card-title>
      <v-card-text>
        <v-data-table
          show-expand
          :headers="computed_headers"
          :items="items"
          :loading="loading"
          :loading-text="$t('loading')"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="footerProps"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
        >
          <template v-slot:expanded-item="{ headers: _headers, item }">
            <td :colspan="_headers.length" class="ma-3 pa-6">
              <v-list dense>
                <v-subheader>{{ $t('fields.membership_type') }}:</v-subheader>
                <v-list-item-group
                  color="primary"
                >
                  <v-list-item
                    v-for="(membership_name, i) in item.membership_names"
                    :key="i"
                  >
                    <v-list-item-content>
                      <v-list-item-title v-text="membership_name.name" />
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </td>
          </template>
          <template #item.scheduled_at="{item}">
            {{ item.scheduled_at | dateFormat }}
          </template>
          <template #item.transaction_type="{item}">
            {{ $t('types.'+item.transaction_type.name) }}
          </template>
          <template #item.amount="{item}">
            {{ item.amount | numberFormat }} {{ profile.organization.currency.name }}
          </template>
          <template #item.actions="{item}">
            <tooltip-btn
              v-for="(action, i) in actions"
              :key="i"
              :show="!action.disable"
              :color="action.color"
              :icon="action.icon"
              :tooltip="action.tooltip"
              @click="action.func(item.id)"
            />
            <tooltip-btn
              v-if="editor && item.scheduled_at === null"
              color="edit"
              icon="$edit"
              tooltip="tooltip_edit"
              @click="edit(item.id)"
            />
            <tooltip-btn
              v-if="item.scheduled_at === null"
              color="run"
              icon="$run"
              tooltip="tooltip_run"
              @click="runSchedule(item.id)"
            />
            <tooltip-btn
              v-if="item.scheduled_at !== null"
              color="rollback"
              icon="$rollback"
              tooltip="tooltip_rollback"
              @click="rollbackSchedule(item.id)"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  // Utilities
  import { get, call, sync } from 'vuex-pathify'

  export default {
    name: 'ScheduledTransactions',
    data () {
      return {
        expanded: [],
        singleExpand: true,
        items: [],
        // table
        footerProps: {
          itemsPerPageOptions: [10, 20, 25, 50, 100],
        },
        total: 0,
        timer: null,
        show_scheduled: false,
      }
    },
    computed: {
      loading: get('app/loading'),
      profile: get('auth/profile'),
      options: sync('scheduledTransaction/options'),
      search: sync('scheduledTransaction/search'),
      headers () {
        return [
          {
            hide_small: true,
            text: this.$t('fields.name'),
            align: 'start',
            sortable: false,
            value: 'name',
          },
          {
            text: this.$t('fields.transaction_type'),
            value: 'transaction_type',
          },
          {
            text: this.$t('fields.amount'),
            align: 'start',
            sortable: true,
            width: 140,
            value: 'amount',
          },
          {
            text: this.$t('fields.scheduled_date'),
            align: 'start',
            sortable: false,
            value: 'scheduled_date',
          },
          {
            text: this.$t('fields.scheduled_at'),
            align: 'start',
            sortable: false,
            value: 'scheduled_at',
          },
          {
            // text: this.$t('actions'),
            value: 'actions',
            sortable: false,
            align: 'right',
            width: 150,
          },
        ]
      },
      actions () {
        return [
          // { color: 'info', icon: '$info' },
          // { color: 'edit', icon: '$edit', tooltip: 'tooltip_edit', func: this.edit, disable: !this.editor },
        ]
      },
      page_title () { return this.$t('scheduled_transactions') },
      editor () { return this.profile.can['transaction-edit'] },
      computed_headers () { return this.headers.filter(h => h.hide_small !== this.$vuetify.breakpoint.mdAndDown) },
    },
    watch: {
      search: {
        handler () {
          if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
          }
          this.timer = setTimeout(() => {
            this.load()
          }, 800)
        },
        deep: true,
      },
      options: {
        handler () {
          this.load()
        },
        deep: true,
      },
      show_scheduled: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },
    mounted () {
    },
    methods: {
      getList: call('scheduledTransaction/search'),
      run: call('scheduledTransaction/run'),
      rollback: call('scheduledTransaction/rollback'),
      async load () {
        try {
          const { sortBy, sortDesc, page, itemsPerPage } = this.options
          await this.getList({
            page: page,
            per_page: itemsPerPage,
            sort: {
              field: sortBy,
              order: sortDesc,
            },
            search: this.search,
            show_scheduled: this.show_scheduled,
          }).then(response => {
            this.items = response.data
            this.total = response.total
          }).catch(e => {
            console.log(e)
          })
        } catch (e) {
        }
      },
      edit (id) {
        let url = ''
        if (id === 'new') {
          url += '/scheduled-transaction/new'
        } else {
          url += `/scheduled-transaction/edit/${id}`
        }
        this.$router.push(url).catch(() => {})
      },
      async runSchedule (id) {
        await this.run({ id: id }).then(response => {
          this.load()
        }).catch(e => {
          console.log(e)
        })
      },
      async rollbackSchedule (id) {
        await this.rollback({ id: id }).then(response => {
          this.load()
        }).catch(e => {
          console.log(e)
        })
      },
      storageUrl (transaction) {
        return process.env.VUE_APP_API_URL + '/api/storage/' + transaction.storage_id
      },
      balanceClass (value) {
        let result = 'd-inline '
        result += (value < 0 ? 'red--text' : 'green--text')
        return result
      },
      back () {
        this.$router.push('/transactions/').catch(() => {})
      },
    },
  }
</script>
